<script setup lang="ts">
    import { type Table } from '@tanstack/vue-table';
    import { computed } from 'vue';
    import {
        Select,
        SelectContent,
        SelectItem,
        SelectTrigger,
        SelectValue,
    } from '@/components/ui/select';
    import { Separator } from '@/components/ui/separator';
    import { AcceptableValue } from 'reka-ui';
    import DataTablePagination from '@/components/datatable/DataTablePagination.vue';

    interface DataTableOptionsProps {
        table: Table<any>;
        hideControls?: boolean;
        manualPagination?: boolean;
        totalRecords?: number;
    }
    const props = withDefaults(defineProps<DataTableOptionsProps>(), {
        hideControls: false,
        manualPagination: false,
        totalRecords: undefined,
    });

    const totalRows = computed(() => {
        if (props.manualPagination && props.totalRecords !== undefined) {
            return props.totalRecords;
        }
        return props.table.getFilteredRowModel().flatRows.filter((row) => !row.subRows?.length)
            .length;
    });

    const selectedRows = computed(() => {
        return props.table.getFilteredSelectedRowModel().flatRows.length;
    });
</script>

<template>
    <div class="flex items-center gap-4">
        <div
            class="flex items-center gap-4"
            v-if="!!selectedRows"
        >
            <span class="text-xs">
                <span class="font-bold">{{ selectedRows }}</span>
                {{ $t('datatables.of') }}
                <span class="font-bold">{{ totalRows }}</span>
                {{ $t('datatables.rows_selected') }}
            </span>
            <Separator
                orientation="vertical"
                class="h-2.5!"
            />
        </div>
        <Select
            v-if="!hideControls"
            :model-value="table.getState().pagination.pageSize"
            @update:model-value="(value: AcceptableValue) => table.setPageSize(Number(value))"
        >
            <SelectTrigger class="h-8 cursor-pointer gap-1 border-none px-0 shadow-none">
                {{ $t('datatables.show') }}
                <SelectValue
                    :placeholder="table.getState().pagination.pageSize.toString()"
                    class="font-bold"
                />
                {{ $t('datatables.rows') }}
            </SelectTrigger>
            <SelectContent side="top">
                <SelectItem
                    v-for="pageSize in [10, 20, 40, 60, 80, 100]"
                    :key="pageSize"
                    :value="pageSize"
                >
                    {{ pageSize }}
                </SelectItem>
            </SelectContent>
        </Select>
        <Separator
            v-if="!hideControls"
            orientation="vertical"
            class="h-2.5!"
        />

        <span class="text-xs">
            <span class="font-bold">
                {{ totalRows }}
            </span>
            {{ $t('datatables.entries') }}
        </span>
        <div
            v-if="!hideControls"
            class="ml-auto flex items-center justify-center text-xs font-medium"
        >
            {{ $t('datatables.page') }} {{ table.getState().pagination.pageIndex + 1 }}
            {{ $t('datatables.of') }} {{ table.getPageCount() }}
        </div>
        <Separator
            v-if="!hideControls"
            orientation="vertical"
            class="h-2.5!"
        />
        <DataTablePagination
            v-if="!hideControls"
            :table="table"
            :total-records="totalRows"
        />
    </div>
</template>
